import { Head, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import { I18nProvider, useI18n } from '../pages/marketing/i18n'
import ChatWidget from '../pages/marketing/shell/ChatWidget'
import CookieConsent from '../pages/marketing/shell/CookieConsent'
import Footer from '../pages/marketing/shell/Footer'
import Nav from '../pages/marketing/shell/Nav'
import PreviewBanner from '../pages/marketing/shell/PreviewBanner'
import { PreviewCollectionsProvider } from '../pages/marketing/shell/PreviewCollections'

/**
 * Public marketing shell, injected on every Marketing::* page (mirrors the
 * nav/footer chrome from mockups/marketing/site.js). Light theme only — no
 * theme toggle, no dark mode (a deliberate marketing product decision). Wraps
 * the tree in the trilingual I18nProvider so language persists across Inertia
 * visits with no reload. The live-chat widget and cookie-consent banner (0007)
 * mount at the end of the shell.
 */
const SITE = 'https://geo4logistics.com'
const OG_IMAGE = `${SITE}/og-default.png`

function ShellChrome({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  // Canonical/OG URL is the path without query (e.g. /track?q=… canonicalises to /track).
  const canonical = `${SITE}${usePage().url.split('?')[0]}`
  return (
    <>
      <Head>
        <link rel="canonical" href={canonical} />
        <meta property="og:site_name" content="Geo4Logistics" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Head>
      <a className="skip-link" href="#main">
        {t('a11y.skip')}
      </a>
      <PreviewBanner />
      <Nav />
      <main id="main">{children}</main>
      <Footer />
      <ChatWidget />
      <CookieConsent />
    </>
  )
}

export default function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <I18nProvider>
      <PreviewCollectionsProvider>
        <ShellChrome>{children}</ShellChrome>
      </PreviewCollectionsProvider>
    </I18nProvider>
  )
}
